import { StoreData } from "@/types/inbox/inbox.types";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

export type ConfirmationModalType = null | "close_chat" | "leave_chat";

export type InboxStore = {
    unreadCount: Record<"my" | "internal" | number | string, number>;
    setUnreadCount: (record: Record<"my" | "internal" | number | string, number>) => void;
    store: StoreData;
    setStore: (data: Partial<StoreData>) => void;

    confirmationModalType: ConfirmationModalType;
    setConfirmationModalType: (type: ConfirmationModalType, callback: (() => void) | null) => void;
    confirmationModalCallback: (() => void) | null;

    chatInitialized: boolean;
    setChatInitialized: (initialized: boolean) => void;
};

export const useInboxStore = create(
    immer<InboxStore>((set) => ({
        unreadCount: {
            my: 0,
            internal: 0,
        },
        store: {},
        setUnreadCount: (record: Record<"my" | "internal" | number | string, number>) =>
            set((state) => {
                state.unreadCount = record;
            }),
        setStore: (data: Partial<StoreData>) =>
            set((state) => {
                state.store = { ...state.store, ...data };
            }),

        confirmationModalType: null,
        setConfirmationModalType: (type: ConfirmationModalType, callback: (() => void) | null) =>
            set((state) => {
                state.confirmationModalType = type;
                state.confirmationModalCallback = callback;
            }),
        confirmationModalCallback: () => {
            set((state) => {
                state.confirmationModalType = null;
                state.confirmationModalCallback = null;
            });
        },

        chatInitialized: false,
        setChatInitialized: (initialized: boolean) => {
            set((state) => {
                state.chatInitialized = initialized;
            });
        },
    }))
);

/** Ask the dashboard's confirmation dialog, and resolve with the answer. The messenger's `confirm`
 *  prop is this: legacy sent Close and Leave to the host's dialog rather than acting on the click,
 *  and dismissing it did nothing. */
let pendingConfirmation: ((answer: boolean) => void) | null = null;

export function askInboxConfirmation(type: NonNullable<ConfirmationModalType>): Promise<boolean> {
    // One dialog, one question. A second ask overwrites the first's callback, so the first is
    // answered NO here rather than left to resolve on somebody else's dialog closing.
    pendingConfirmation?.(false);
    return new Promise((resolve) => {
        let confirmed = false;
        // The dialog's Yes runs this, and nothing else does — so its identity in the store is also
        // how we know the question on screen is OURS.
        const onConfirm = () => {
            confirmed = true;
        };
        const settle = (answer: boolean) => {
            if (pendingConfirmation !== settle) return;
            pendingConfirmation = null;
            unsubscribe();
            resolve(answer);
        };
        pendingConfirmation = settle;
        // Only OUR question coming down answers this promise. zustand notifies on every `set`, and
        // this store carries the sidebar's unread badge as well — so without the latch a badge
        // refresh landing in the tick before the deferred open below (a frame, a tab focus) read as
        // "the dialog closed", answered NO, and left the agent clicking Yes on a dialog the
        // messenger had already stopped listening to.
        let asked = false;
        const unsubscribe = useInboxStore.subscribe((state) => {
            if (state.confirmationModalCallback === onConfirm) {
                asked = true;
                return;
            }
            // Ours is gone: the dialog clears the pair on confirm, cancel and dismiss alike, and
            // only `onConfirm` having run distinguishes them.
            if (asked) settle(confirmed);
        });
        // ONE TICK LATE, deliberately. The question is nearly always asked from inside a Radix menu
        // item's onSelect, and that menu is still tearing down: both it and the dialog take
        // `pointer-events` off <body> while they are up and put it back on the way out, so opening
        // the dialog inside that teardown makes the menu restore "none" — the whole app is then
        // click-dead until a reload, after Yes and after No alike. Letting the menu finish first
        // costs a frame nobody sees.
        setTimeout(() => {
            useInboxStore.getState().setConfirmationModalType(type, onConfirm);
        }, 0);
    });
}

export const NOTIFICATION_EVENTS = {
    CLOSE_CHAT: "CLOSE_CHAT",
    LEAVE_CHAT: "LEAVE_CHAT",
    MESSENGER_NOTIFICATION_CONFIG_UPDATE: "MESSENGER_NOTIFICATION_CONFIG_UPDATE",
};
